import {
  MessageGroupProps,
  MessagePart,
  SmartActionAiMessage,
  ToolInvocation,
} from '@bitte-ai/types';
import { Check, Copy } from 'lucide-react';
import { memo, useState } from 'react';

import { ImprovedMessage } from './ImprovedMessage';
import DefaultMessageContainer from './default-components/DefaultMessageContainer';
import { useMessageAgentId } from './hooks/useMessageAgentId';
import { ToolCall } from './message/ToolCall';

export const MessageGroup = memo(
  ({
    groupKey,
    messages,
    accountId,
    creator,
    agentImage,
    agentName,
    messageBackgroundColor,
    borderColor,
    textColor,
    chatId,
    append,
    customMessageContainer: MessageContainer = DefaultMessageContainer,
    customTxContainer,
    customApproveTxButton,
    customDeclineTxButton,
    customToolComponents,
    showBorder,
    hideToolCall,
    isLoading,
  }: MessageGroupProps) => {
    const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

    const messagesWithAgentId = useMessageAgentId(
      messages,
      agentImage,
      agentName,
    );

    const copyToClipboard = async (content: string, messageId: string) => {
      try {
        await navigator.clipboard.writeText(content);
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    };

    return messagesWithAgentId?.map((message, index) => {
      if (message.role === 'system') return null;

      const uniqueKey = `${groupKey}-${index}`;
      const isUser = message.role === 'user';
      const userName = creator || accountId || '';

      const toolInvocations =
        message.parts?.filter(
          (part): part is MessagePart & { toolInvocation: ToolInvocation } =>
            part.type === 'tool-invocation' && !!part.toolInvocation,
        ) || [];

      return (
        <MessageContainer
          key={`${message.id}-${message.role}-${index}`}
          message={message}
          isUser={isUser}
          userName={userName}
          style={{
            backgroundColor: messageBackgroundColor,
            borderColor: borderColor,
            textColor: textColor,
          }}
          uniqueKey={uniqueKey}
          showBorder={showBorder}
        >
          <div
            className={` ${
              isUser
                ? 'bitte-w-auto'
                : 'bitte-w-full bitte-mt-6 bitte-flex bitte-flex-col bitte-gap-2'
            } `}
          >
            {message.content && !isUser && (
              <div className="bitte-flex bitte-flex-col bitte-gap-4">
                <div className="bitte-relative">
                  <ImprovedMessage
                    content={
                      typeof message.content === 'string'
                        ? message.content
                        : JSON.stringify(message.content)
                    }
                    onAppend={append}
                    isComplete={!isLoading}
                    isStreaming={isLoading}
                  />

                  {/* Copy button */}
                  {!isLoading && (
                    <div className="bitte-flex bitte-justify-start bitte-mt-2">
                      <button
                        type="button"
                        onClick={() =>
                          copyToClipboard(
                            typeof message.content === 'string'
                              ? message.content
                              : JSON.stringify(message.content),
                            message.id || '',
                          )
                        }
                        className="bitte-p-1 bitte-rounded bitte-transition-all bitte-duration-200 hover:bitte-bg-gray-600/50 bitte-text-white"
                        title={
                          copiedMessageId === message.id
                            ? 'Copied!'
                            : 'Copy message'
                        }
                      >
                        {copiedMessageId === message.id ? (
                          <Check className="bitte-w-3 bitte-h-3" />
                        ) : (
                          <Copy className="bitte-w-3 bitte-h-3" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {message.content && isUser && (
              <div className="bitte-flex bitte-flex-col bitte-gap-4">
                <div className="bitte-whitespace-pre-wrap">
                  {typeof message.content === 'string'
                    ? message.content
                    : JSON.stringify(message.content)}
                </div>
              </div>
            )}

            {toolInvocations.map((part) => (
              <ToolCall
                key={`${part.toolInvocation.toolCallId}-${part.toolInvocation.toolName}`}
                toolInvocation={part.toolInvocation}
                hideToolCall={hideToolCall}
                showBorder={showBorder}
                messageId={message.id || ''}
                groupKey={groupKey}
                chatId={chatId || ''}
                creator={creator}
                borderColor={borderColor}
                messageBackgroundColor={messageBackgroundColor}
                textColor={textColor}
                customApproveTxButton={customApproveTxButton}
                customDeclineTxButton={customDeclineTxButton}
                customTxContainer={customTxContainer}
                append={append}
                customToolComponents={customToolComponents}
              />
            ))}
          </div>
        </MessageContainer>
      );
    });
  },
  (prevProps, nextProps) => {
    // Quick checks for obvious differences
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.groupKey !== nextProps.groupKey) return false;
    if (prevProps.messages.length !== nextProps.messages.length) return false;

    // For performance, if we have the same message array reference, skip deep comparison
    if (prevProps.messages === nextProps.messages) {
      return true;
    }

    // CRITICAL: For smooth streaming, reduce re-render frequency
    // Only re-render every few content changes during streaming
    const currentContent = prevProps.messages[0]?.content || '';
    const nextContent = nextProps.messages[0]?.content || '';

    if (prevProps.isLoading && nextProps.isLoading) {
      // During streaming, only re-render on significant content changes (every ~20 characters)
      const contentDifference = Math.abs(
        nextContent.length - currentContent.length,
      );
      if (contentDifference < 20 && nextContent.startsWith(currentContent)) {
        return true; // Skip re-render for small incremental changes
      }
    }

    // Deep compare messages only if length is the same
    for (let i = 0; i < prevProps.messages.length; i++) {
      const prevMsg = prevProps.messages[i];
      const nextMsg = nextProps.messages[i];

      if (prevMsg.id !== nextMsg.id) return false;
      if (prevMsg.content !== nextMsg.content) return false;
      if (prevMsg.role !== nextMsg.role) return false;

      // Compare parts if they exist - but be more optimized for tool invocations
      const prevParts = (prevMsg as SmartActionAiMessage).parts;
      const nextParts = (nextMsg as SmartActionAiMessage).parts;

      if (prevParts?.length !== nextParts?.length) return false;
      if (prevParts && nextParts) {
        for (let j = 0; j < prevParts.length; j++) {
          const prevPart = prevParts[j];
          const nextPart = nextParts[j];

          if (prevPart.type !== nextPart.type) return false;

          // For tool invocations, be more granular about what triggers re-renders
          if (
            prevPart.type === 'tool-invocation' &&
            nextPart.type === 'tool-invocation'
          ) {
            const prevTool = prevPart.toolInvocation;
            const nextTool = nextPart.toolInvocation;

            if (!prevTool || !nextTool) return false;
            if (prevTool.toolCallId !== nextTool.toolCallId) return false;
            if (prevTool.toolName !== nextTool.toolName) return false;

            // Only re-render if there's a meaningful state change
            const prevHasResult = prevTool.result && !prevTool.result.error;
            const nextHasResult = nextTool.result && !nextTool.result.error;
            const prevHasError = prevTool.result && prevTool.result.error;
            const nextHasError = nextTool.result && nextTool.result.error;

            // Re-render if we transition from no result to having result, or vice versa
            if (prevHasResult !== nextHasResult) return false;
            if (prevHasError !== nextHasError) return false;

            // Re-render if the actual result data changed (but not for state transitions)
            if (prevTool.result !== nextTool.result) {
              if (prevTool.result?.data !== nextTool.result?.data) return false;
              if (prevTool.result?.error !== nextTool.result?.error)
                return false;
            }
          }
        }
      }
    }

    // Props are equal, skip re-render
    return true;
  },
);
